import { useState, useEffect } from 'react';
import { Button } from '@/shared/components/ui/Button';
import { Input } from '@/shared/components/ui/Input';
import { Card } from '@/shared/components/ui/Card';
import { useToast } from '@/shared/components/ui/Toast';
import { useAuthStore } from '@/stores/authStore';
import {
  useMyPhoneNumber,
  useUpdateMyPhoneNumber,
  useSendTestNotification,
} from '@/features/settings/hooks/useNotificationPreferences';

export function NotificationContactCard() {
  const { showToast } = useToast();
  const { user } = useAuthStore();
  
  // Queries
  const { data: phoneNumber, isLoading: phoneLoading } = useMyPhoneNumber();
  
  // Mutations
  const updatePhoneMutation = useUpdateMyPhoneNumber();
  const testNotificationMutation = useSendTestNotification();
  
  // Local state
  const [phoneInput, setPhoneInput] = useState('');
  const [phoneError, setPhoneError] = useState('');
  const [additionalEmails, setAdditionalEmails] = useState<string[]>([]);
  const [newEmailInput, setNewEmailInput] = useState('');
  const [emailError, setEmailError] = useState('');

  // Initialize phone input when data loads
  useEffect(() => {
    if (phoneNumber) {
      setPhoneInput(phoneNumber);
    }
  }, [phoneNumber]);

  // Format phone number as user types
  const handlePhoneChange = (value: string) => {
    // Strip non-digits
    const digits = value.replace(/\D/g, '');
    
    // Format as +1XXXXXXXXXX
    if (digits.length === 0) {
      setPhoneInput('');
      setPhoneError('');
    } else if (digits.length <= 11) {
      // Add +1 prefix if not present
      const formatted = digits.startsWith('1') 
        ? `+${digits}` 
        : `+1${digits}`;
      setPhoneInput(formatted);
      
      // Validate
      if (formatted.length === 12) {
        setPhoneError('');
      } else {
        setPhoneError('Phone number must be 10 digits');
      }
    }
  };

  // Save phone number
  const handleSavePhone = async () => {
    const phoneToSave = phoneInput.trim() || null;
    
    if (phoneToSave && !/^\+1[0-9]{10}$/.test(phoneToSave)) {
      setPhoneError('Phone number must be in format +1XXXXXXXXXX');
      return;
    }

    try {
      await updatePhoneMutation.mutateAsync(phoneToSave);
      showToast('Phone number updated successfully', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to update phone number',
        'error'
      );
    }
  };

  // Clear phone number
  const handleClearPhone = async () => {
    try {
      await updatePhoneMutation.mutateAsync(null);
      setPhoneInput('');
      showToast('Phone number removed', 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : 'Failed to remove phone number',
        'error'
      );
    }
  };

  // Send test notification
  const handleTestNotification = async (channel: 'email' | 'sms') => {
    if (channel === 'sms' && !phoneNumber) {
      showToast('Please add a phone number first', 'error');
      return;
    }

    try {
      await testNotificationMutation.mutateAsync(channel);
      showToast(`Test ${channel} notification sent!`, 'success');
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : `Failed to send test ${channel}`,
        'error'
      );
    }
  };

  // Validate email format
  const isValidEmail = (email: string) => {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  };

  // Add additional email
  const handleAddEmail = () => {
    const email = newEmailInput.trim().toLowerCase();
    
    if (!email) {
      setEmailError('Please enter an email address');
      return;
    }
    
    if (!isValidEmail(email)) {
      setEmailError('Please enter a valid email address');
      return;
    }
    
    if (email === user?.email?.toLowerCase()) {
      setEmailError('This is already your login email');
      return;
    }
    
    if (additionalEmails.includes(email)) {
      setEmailError('This email is already added');
      return;
    }
    
    setAdditionalEmails([...additionalEmails, email]);
    setNewEmailInput('');
    setEmailError('');
    showToast('Email added', 'success');
  };

  // Remove additional email
  const handleRemoveEmail = (email: string) => {
    setAdditionalEmails(additionalEmails.filter(e => e !== email));
    showToast('Email removed', 'success');
  };

  if (phoneLoading) {
    return (
      <Card className="p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6">
      <div className="flex items-center gap-2 mb-4">
        <svg className="w-5 h-5 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900">
          My Notification Contact
        </h3>
      </div>

      <div className="space-y-6">
        {/* Email Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Email Addresses
          </label>
          
          {/* Primary email (login) */}
          <div className="flex items-center gap-2 mb-2">
            <div className="flex-1 px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
              {user?.email}
            </div>
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded">
              Login email
            </span>
          </div>
          
          {/* Additional emails */}
          {additionalEmails.map((email) => (
            <div key={email} className="flex items-center gap-2 mb-2">
              <div className="flex-1 px-3 py-2 bg-white border border-gray-200 rounded-md text-gray-700">
                {email}
              </div>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => handleRemoveEmail(email)}
              >
                Remove
              </Button>
            </div>
          ))}
          
          {/* Add email input */}
          <div className="flex items-start gap-2 mt-3">
            <div className="flex-1">
              <Input
                type="email"
                placeholder="Add another email address"
                value={newEmailInput}
                onChange={(e) => {
                  setNewEmailInput(e.target.value);
                  setEmailError('');
                }}
                error={emailError}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddEmail();
                  }
                }}
              />
            </div>
            <Button
              variant="secondary"
              onClick={handleAddEmail}
            >
              Add
            </Button>
          </div>
        </div>

        {/* Phone Section */}
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">
            Phone Number for SMS
          </label>
          <p className="text-xs text-gray-500 mb-2">
            US phone numbers only. Format: +1XXXXXXXXXX
          </p>
          
          <div className="flex items-start gap-2">
            <div className="flex-1 max-w-xs">
              <Input
                type="tel"
                placeholder="+1 (555) 123-4567"
                value={phoneInput}
                onChange={(e) => handlePhoneChange(e.target.value)}
                error={phoneError}
              />
            </div>
            <Button
              onClick={handleSavePhone}
              disabled={updatePhoneMutation.isPending || !!phoneError || phoneInput === (phoneNumber || '')}
            >
              {updatePhoneMutation.isPending ? 'Saving...' : 'Save'}
            </Button>
            {phoneNumber && (
              <Button
                variant="secondary"
                onClick={handleClearPhone}
                disabled={updatePhoneMutation.isPending}
              >
                Remove
              </Button>
            )}
          </div>
        </div>

        {/* Test Buttons */}
        <div className="flex items-center gap-3 pt-4 border-t border-gray-200">
          <span className="text-sm text-gray-600">Send test:</span>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleTestNotification('email')}
            disabled={testNotificationMutation.isPending}
          >
            📧 Test Email
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => handleTestNotification('sms')}
            disabled={testNotificationMutation.isPending || !phoneNumber}
            title={!phoneNumber ? 'Add a phone number first' : ''}
          >
            📱 Test SMS
          </Button>
        </div>
      </div>
    </Card>
  );
}

